/**
 * FlashBook recovery offer.
 *
 * The studio keeps a copy of the book as it is written, and this is where it
 * hands it back. It is a dialog rather than a banner because it is a *decision*
 * with two real answers — the creator's work, or a clean sheet — and a banner can
 * be scrolled past, which would leave the copy sitting on the device until
 * something else overwrote it.
 *
 * The offer never says "unsaved changes", because that is not what happened: a
 * session ended while the book was still being written. It says what is here, how
 * old it is, and the one thing that may not have come back with it.
 */
import {View} from 'react-native'
import {Trans, useLingui} from '@lingui/react/macro'

import {
  describeSnapshotAge,
  type FlashBookSnapshot,
  unbackedMediaCount,
} from '#/lib/flashbookRecovery'
import {atoms as a, useTheme, web} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import * as Dialog from '#/components/Dialog'
import {Text} from '#/components/Typography'

export function FlashBookRecoveryDialog({
  control,
  snapshot,
  onRestore,
  onDiscard,
}: {
  control: Dialog.DialogControlProps
  /** The copy on offer, or `null` when there is nothing to hand back. */
  snapshot: FlashBookSnapshot | null
  onRestore: () => void
  onDiscard: () => void
}) {
  return (
    <Dialog.Outer
      control={control}
      nativeOptions={{preventExpansion: true}}
      webOptions={{alignCenter: true}}>
      <Dialog.Handle />
      {snapshot ? (
        <RecoveryBody
          snapshot={snapshot}
          onRestore={onRestore}
          onDiscard={onDiscard}
        />
      ) : null}
    </Dialog.Outer>
  )
}

function RecoveryBody({
  snapshot,
  onRestore,
  onDiscard,
}: {
  snapshot: FlashBookSnapshot
  onRestore: () => void
  onDiscard: () => void
}) {
  const t = useTheme()
  const {t: l} = useLingui()
  const control = Dialog.useDialogContext()

  const age = describeSnapshotAge(snapshot.savedAt)
  const when =
    age === 'moment'
      ? l`a moment ago`
      : age === 'minutes'
        ? l`a few minutes ago`
        : age === 'hours'
          ? l`earlier today`
          : age === 'days'
            ? l`a few days ago`
            : l`a while ago`

  const unbacked = unbackedMediaCount(snapshot)
  const title = snapshot.book.title.trim() || l`Untitled book`

  return (
    <Dialog.ScrollableInner
      style={web({maxWidth: 420})}
      label={l`Unfinished FlashBook`}>
      <View style={[a.gap_2xl]}>
        <View style={[a.gap_sm]}>
          <Text style={[a.font_bold, a.text_2xl]}>
            <Trans>Pick up your book?</Trans>
          </Text>
          <Text
            style={[t.atoms.text_contrast_medium, a.text_md, a.leading_snug]}>
            <Trans>
              {title} was still being written when this device last stopped. The
              copy taken {when} is still here.
            </Trans>
          </Text>
          {/*
            Only when there is something at stake. A book with no local file in
            it comes back whole, and a warning that is always shown is a warning
            nobody reads on the day it matters.
          */}
          {unbacked > 0 ? (
            <Text
              style={[t.atoms.text_contrast_medium, a.text_md, a.leading_snug]}
              testID="flashBookRecoveryFiles">
              <Trans>
                Everything you wrote comes back. A file it uses was never
                uploaded, so you may be asked to pick it again.
              </Trans>
            </Text>
          ) : null}
        </View>

        <View style={[a.gap_sm]}>
          <Button
            label={l`Restore this book`}
            accessibilityHint={l`Puts the copy back so you can carry on`}
            onPress={() => {
              onRestore()
              control.close()
            }}
            size="large"
            variant="solid"
            color="primary"
            testID="flashBookRecoveryRestoreBtn">
            <ButtonText>
              <Trans>Restore it</Trans>
            </ButtonText>
          </Button>
          <Button
            label={l`Discard the copy`}
            accessibilityHint={l`Starts a fresh book and deletes the copy`}
            onPress={() => {
              onDiscard()
              control.close()
            }}
            size="large"
            variant="solid"
            color="negative_subtle"
            testID="flashBookRecoveryDiscardBtn">
            <ButtonText>
              <Trans>Start fresh</Trans>
            </ButtonText>
          </Button>
        </View>
      </View>
    </Dialog.ScrollableInner>
  )
}
